import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { AlarmClock, BellRing, Check, Loader2, Lock, MessageSquare, Pencil, ShieldAlert, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fmtDateTime, humanize } from "@/lib/api";
import { useWorkspace, useWsQuery } from "@/context/WorkspaceContext";
import { ConfidenceBadge, EmptyState, ErrorState, Loading, PageHeader, QueryState, RiskBadge, StatusBadge, WaitBadge } from "@/components/common";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const FILTERS = [["open", "Open"], ["pending", "Pending"], ["approved", "Approved"], ["changes_requested", "Changes requested"], ["rejected", "Rejected"], ["snoozed", "Snoozed"], ["cancelled", "Cancelled"], ["all", "All"]];
const OPEN = ["pending", "snoozed", "changes_requested"];

function Meta({ label, children }) {
  return <div><div className="cq-muted-label mb-1">{label}</div><div className="text-sm text-[#F7FAFF]">{children || "—"}</div></div>;
}

function ReviewerPicker({ a }) {
  const { path, can, refresh } = useWorkspace();
  const q = useWsQuery("/approvals/reviewers");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);
  const open = ["pending", "snoozed", "changes_requested"].includes(a.status);
  const editable = open && (can("edit") || can("approve"));
  const change = async (e) => {
    const uid = e.target.value || null;
    setBusy(true); setPending(uid ?? "");
    try { await api.put(path(`/approvals/${a.id}/reviewer`), { user_id: uid }); toast.success(uid ? "Reviewer assigned. Reminders now go only to them." : "Unassigned. Reminders go to every reviewer."); refresh(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); setPending(null); }
  };
  if (!editable) return <span data-testid="approval-reviewer-display" className="text-xs text-[#AAB6CC]">Reviewer: <span className="text-[#F7FAFF]">{a.assigned_reviewer_name || "Any reviewer"}</span></span>;
  return (
    <label className="inline-flex items-center gap-2 text-xs text-[#AAB6CC]">Reviewer
      <select data-testid="approval-reviewer-select" disabled={busy || q.isLoading} value={pending ?? (a.assigned_reviewer_id || "")} onChange={change}
        className="cq-input !py-1 !px-2 !text-xs !w-auto">
        <option value="">Any reviewer</option>
        {(q.data || []).map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
      </select>
      {busy && <Loader2 size={12} className="animate-spin" />}
    </label>
  );
}

function NudgeButton({ a }) {
  const { path, refresh } = useWorkspace();
  const [busy, setBusy] = useState(false);
  const recent = a.last_nudged_at && Date.now() - new Date(a.last_nudged_at) < 20 * 3600000;
  const go = async () => {
    setBusy(true);
    try { const { data } = await api.post(path(`/approvals/${a.id}/nudge`)); toast.success(`Reminder sent to ${data.reviewers.join(", ") || "no reviewers"}`); refresh(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <button data-testid="approval-nudge-button" disabled={busy || recent} onClick={go} title={recent ? "Reviewers were reminded in the last day" : a.assigned_reviewer_name ? `Remind ${a.assigned_reviewer_name}` : "Remind everyone who can decide"}
      className="cq-btn cq-btn-ghost !py-1 !px-2.5 !text-xs"><BellRing size={12} /> {recent ? "Reminded recently" : "Nudge reviewers"}</button>
  );
}

function ApprovalDetail({ id, onChanged }) {
  const { path, wid, can } = useWorkspace();
  const qc = useQueryClient();
  const q = useWsQuery(`/approvals/${id}`);
  const [comment, setComment] = useState("");
  const [mode, setMode] = useState(null);
  const [edits, setEdits] = useState({});
  const [snooze, setSnooze] = useState(3);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setMode(null); setComment(""); setEdits({}); }, [id]);

  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorState message={apiError(q.error)} />;
  const a = q.data;
  const open = OPEN.includes(a.status);
  const highImpact = ["high", "critical"].includes(a.risk_level);
  const blocked = highImpact && a.confidence_level !== "high";
  const editable = ["content", "creative"].includes(a.item_type) && a.item;

  const run = async (action, extra = {}) => {
    setBusy(true);
    try {
      await api.post(path(`/approvals/${id}/action`), { action, comment, confirm: highImpact, snooze_days: snooze, ...extra });
      toast.success(`Decision recorded: ${humanize(action)}`);
      setMode(null); setComment("");
      qc.invalidateQueries({ queryKey: ["ws", wid] }); onChanged?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const approve = () => (highImpact ? setConfirmOpen(true) : run(mode === "edit" ? "edit_approve" : "approve", mode === "edit" ? { edits } : {}));
  const addComment = async () => {
    try { await api.post(path(`/approvals/${id}/comments`), { body: comment }); setComment(""); q.refetch(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="approval-detail" className="cq-card p-5 sm:p-7 space-y-6">
      <div>
        <div className="flex flex-wrap items-center gap-2 mb-3"><StatusBadge status={a.status} testId="approval-status" /><RiskBadge level={a.risk_level} /><ConfidenceBadge level={a.confidence_level} />{a.is_demo && <span className="font-mono text-[10px] text-[#F4B860]">DEMO</span>}</div>
        <h2 className="font-display text-2xl font-semibold text-[#F7FAFF]">{a.item_title}</h2>
        <p className="mt-1 text-sm text-[#AAB6CC]">Requested action: <span className="text-[#F7FAFF]">{a.requested_action}</span></p>
        <div className="mt-3 flex flex-wrap items-center gap-3"><WaitBadge a={a} testId="approval-detail-wait" />{a.status !== "cancelled" && <ReviewerPicker a={a} />}
          {["pending", "snoozed", "changes_requested"].includes(a.status) && <NudgeButton a={a} />}</div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
        <Meta label="Item type">{humanize(a.item_type)}</Meta><Meta label="Campaign">{a.campaign_name}</Meta><Meta label="Decided by">{a.reviewer_name || (a.status === "cancelled" ? "Cancelled automatically" : "Not yet decided")}</Meta>
        <Meta label="Created">{fmtDateTime(a.created_at)}</Meta>{a.status === "cancelled" ? <Meta label="Cancelled because">{a.cancel_reason || "Item no longer exists"}</Meta> : <><Meta label="Review due">{fmtDateTime(a.due_at)}</Meta><Meta label="Reviewed">{a.reviewed_at ? fmtDateTime(a.reviewed_at) : "Not yet"}</Meta></>}
        {a.last_nudged_at && <Meta label="Last reminder">{fmtDateTime(a.last_nudged_at)}</Meta>}
        {a.snoozed_until && <Meta label="Snoozed until">{fmtDateTime(a.snoozed_until)}</Meta>}
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="p-4 rounded-lg border border-[#172342] bg-[#050816]/60"><div className="cq-muted-label mb-2">Reason</div><p className="text-sm text-[#D5DDEB]">{a.reason}</p></div>
        <div className="p-4 rounded-lg border border-[#172342] bg-[#050816]/60"><div className="cq-muted-label mb-2">Supporting context</div><p className="text-sm text-[#D5DDEB] whitespace-pre-wrap">{a.supporting_context}</p></div>
      </div>
      {a.payload?.new_date && <p className="text-sm text-[#AAB6CC]">Proposed new date: <span className="font-mono text-[#22E6F2]">{a.payload.new_date}</span></p>}
      {a.item && mode !== "edit" && (
        <div className="p-4 rounded-lg border border-[#172342]"><div className="cq-muted-label mb-2">Content preview · v{a.item.version}</div><div className="text-sm font-medium text-[#F7FAFF]">{a.item.title}</div><p className="text-sm text-[#AAB6CC] mt-2 whitespace-pre-wrap">{a.item.body}</p></div>
      )}
      {mode === "edit" && (
        <div className="space-y-3" data-testid="approval-edit-form">
          <input data-testid="approval-edit-title" className="cq-input" defaultValue={a.item.title} onChange={(e) => setEdits({ ...edits, title: e.target.value })} />
          <textarea data-testid="approval-edit-body" rows={6} className="cq-input" defaultValue={a.item.body} onChange={(e) => setEdits({ ...edits, body: e.target.value })} />
        </div>
      )}
      {blocked && open && (
        <div data-testid="approval-blocked-notice" className="flex gap-3 p-4 rounded-lg border border-[#FF7792]/30 bg-[#FF7792]/5 text-sm text-[#F7FAFF]">
          <ShieldAlert size={16} className="text-[#FF7792] shrink-0 mt-0.5" /> High-impact actions require high-confidence evidence. Approval is disabled; request changes, snooze or reject.
        </div>
      )}
      {a.item_type === "budget_change" && a.status !== "cancelled" && <p className="text-xs text-[#7C8BAD] flex items-center gap-2"><Lock size={12} /> Approving records a decision only. No budget is changed in this build.</p>}

      {open && (
        <div className="space-y-3">
          <textarea data-testid="approval-comment-input" rows={2} className="cq-input" placeholder={mode === "changes" ? "Describe the changes you need (required)" : "Comment (optional)"} value={comment} onChange={(e) => setComment(e.target.value)} />
          {mode === "snooze" && (
            <div className="flex items-center gap-2 text-sm text-[#AAB6CC]">Snooze for
              <select data-testid="approval-snooze-days" className="cq-input !w-24 !py-1.5" value={snooze} onChange={(e) => setSnooze(Number(e.target.value))}>{[1, 3, 7, 14].map((d) => <option key={d} value={d}>{d} days</option>)}</select>
              <button data-testid="approval-snooze-confirm" disabled={busy} onClick={() => run("snooze")} className="cq-btn cq-btn-ghost !py-1.5">Confirm snooze</button>
            </div>
          )}
          {!can("approve") && <p data-testid="approval-no-permission" className="text-xs text-[#7C8BAD]">Your role can comment but not decide. An owner, admin or approver must review this item.</p>}
          <div className="flex flex-wrap gap-2" data-testid="approval-actions">
            {can("approve") && <>
            <button data-testid="approval-approve-button" disabled={busy || blocked} onClick={approve} className="cq-btn cq-btn-success"><Check size={14} /> {mode === "edit" ? "Save edits & approve" : "Approve"}</button>
            {editable && <button data-testid="approval-edit-approve-button" disabled={busy || blocked} onClick={() => setMode(mode === "edit" ? null : "edit")} className="cq-btn cq-btn-ghost"><Pencil size={14} /> {mode === "edit" ? "Stop editing" : "Edit before approving"}</button>}
            <button data-testid="approval-request-changes-button" disabled={busy} onClick={() => (mode === "changes" && comment.trim() ? run("request_changes") : setMode("changes"))} className="cq-btn cq-btn-ghost"><MessageSquare size={14} /> {mode === "changes" ? "Send change request" : "Request changes"}</button>
            <button data-testid="approval-snooze-button" disabled={busy} onClick={() => setMode("snooze")} className="cq-btn cq-btn-ghost"><AlarmClock size={14} /> Snooze</button>
            <button data-testid="approval-reject-button" disabled={busy} onClick={() => run("reject")} className="cq-btn cq-btn-danger"><X size={14} /> Reject</button>
            </>}
            <button data-testid="approval-add-comment-button" disabled={busy || !comment.trim()} onClick={addComment} className="cq-btn cq-btn-ghost ml-auto">Add comment only</button>
          </div>
        </div>
      )}

      <div>
        <div className="cq-muted-label mb-3">Comments & decisions</div>
        {!a.comments?.length ? <p className="text-sm text-[#7C8BAD]">No comments yet.</p> : (
          <div className="space-y-3" data-testid="approval-comments">{a.comments.map((c) => (
            <div key={c.id} className="p-3 rounded-lg bg-[#050816]/60 border border-[#172342]">
              <div className="flex items-center gap-2 text-xs"><span className="text-[#F7FAFF] font-medium">{c.author_name}</span><span className="text-[#7C8BAD]">{humanize(c.kind)} · {fmtDateTime(c.created_at)}</span></div>
              <p className="text-sm text-[#D5DDEB] mt-1">{c.body}</p>
            </div>))}
          </div>)}
      </div>
      <ConfirmDialog open={confirmOpen} onOpenChange={setConfirmOpen} title="Confirm high-impact approval"
        description={`This ${a.risk_level}-risk decision will be recorded in the audit log under your name. Nothing is published or paid for automatically.`}
        confirmLabel="Confirm approval" onConfirm={() => run(mode === "edit" ? "edit_approve" : "approve", mode === "edit" ? { edits } : {})} />
    </div>
  );
}

export default function Approvals() {
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState(params.get("id") ? "all" : "open");
  const q = useWsQuery(`/approvals?status=${filter}`);
  const selected = params.get("id") || q.data?.[0]?.id;

  return (
    <div>
      <PageHeader eyebrow="Approve" title="Approvals" description="Every consequential action waits here for a human decision. Approvals are logged with reviewer, time and comments." />
      <div className="flex flex-wrap gap-2 mb-6" data-testid="approval-filters">
        {FILTERS.map(([v, l]) => (
          <button key={v} data-testid={`approval-filter-${v}`} onClick={() => setFilter(v)} className={`px-3 py-1.5 rounded-lg text-xs border transition-colors ${filter === v ? "border-[#22E6F2]/50 text-[#22E6F2] bg-[#22E6F2]/10" : "border-[#243153] text-[#AAB6CC] hover:text-[#F7FAFF]"}`}>{l}</button>))}
      </div>
      <QueryState q={q} empty={<EmptyState title="No approvals in this view" description="Try another filter." />}>
        {(items) => (
          <div className="grid lg:grid-cols-[minmax(280px,380px)_1fr] gap-6">
            <div className="space-y-2" data-testid="approval-list">
              {items.map((a) => (
                <button key={a.id} data-testid={`approval-item-${a.id}`} onClick={() => setParams({ id: a.id })}
                  className={`w-full text-left p-4 rounded-xl border transition-colors ${selected === a.id ? "border-[#22E6F2]/50 bg-[#0E1733]" : "border-[#243153] bg-[#0A1024] hover:border-[#34457A]"}`}>
                  <div className="flex items-start justify-between gap-2"><span className="text-sm font-medium text-[#F7FAFF]">{a.item_title}</span><StatusBadge status={a.status} /></div>
                  <div className="mt-2 flex flex-wrap items-center gap-2"><RiskBadge level={a.risk_level} /><span className="text-[11px] text-[#7C8BAD]">{humanize(a.item_type)}</span></div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-3"><WaitBadge a={a} testId={`approval-wait-${a.id}`} />{a.assigned_reviewer_name && <span data-testid={`approval-assignee-${a.id}`} className="text-[11px] text-[#AAB6CC]">→ {a.assigned_reviewer_name}</span>}</div>
                </button>))}
            </div>
            {selected && <ApprovalDetail id={selected} />}
          </div>)}
      </QueryState>
    </div>
  );
}
